const SECTION_SELECTOR = '[data-uc-bts-collage]';
const DESKTOP_MQ = '(min-width: 860px)';
const REDUCE_MQ = '(prefers-reduced-motion: reduce)';
const SWATCHES = ['#024A22', '#047223', '#87B18F'];
const INTRO = { duration: 0.7, stagger: 0.1, ease: 'power2.out', threshold: 0.25 };
const FLOAT = { amp: 22, speed: 0.0002, phase: 0.01, ramp: 1.2 };
const DANCE = { amp: 56, followNear: 6.4, followFar: 2.7, maxStep: 0.032 };

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const shuffle = (items) => {
	const out = items.slice();

	for (let i = out.length - 1; i > 0; i -= 1) {
		const j = Math.floor(Math.random() * (i + 1));
		[out[i], out[j]] = [out[j], out[i]];
	}

	return out;
};

const swatchPool = (count) => {
	const each = Math.floor(count / SWATCHES.length);
	const pool = SWATCHES.flatMap((swatch) => Array(each).fill(swatch));

	return shuffle(pool.concat(shuffle(SWATCHES).slice(0, count - pool.length)));
};

const whenGsapReady = (fn) => {
	if (window.gsap) return fn();

	let tries = 0;
	const timer = setInterval(() => {
		if (window.gsap) {
			clearInterval(timer);
			fn();
		} else if ((tries += 1) > 100) {
			clearInterval(timer);
		}
	}, 50);
};

const initSection = (root) => {
	if (root.dataset.ucBtsInit) return;
	root.dataset.ucBtsInit = '1';

	const frame = root.querySelector('[data-uc-bts-frame]');
	if (!frame) return;

	const cards = Array.from(frame.querySelectorAll('[data-uc-bts-card]')).map((el) => ({
		el,
		reveal: el.querySelector('[data-uc-bts-reveal]'),
		swatch: el.querySelector('[data-uc-bts-swatch]'),
		media: Array.from(el.querySelectorAll('img, video, svg')),
		danceX: 0,
		danceY: 0,
		danceAmp: 0,
		followRate: DANCE.followFar,
	}));
	if (!cards.length) return;

	const gsap = window.gsap;
	const desktop = matchMedia(DESKTOP_MQ);
	const reduce = matchMedia(REDUCE_MQ);
	const reveals = cards.map((card) => card.reveal).filter(Boolean);
	const swatches = cards.map((card) => card.swatch).filter(Boolean);
	const media = cards.flatMap((card) => card.media);

	swatchPool(cards.length).forEach((color, i) => {
		cards[i].swatch?.style.setProperty('--bts-swatch', color);
	});

	let ticker = null;
	let elapsed = 0;
	let danceTx = 0;
	let danceTy = 0;
	const ramp = { value: 0 };

	const measure = () => {
		const unit = frame.clientWidth / (desktop.matches ? 1440 : 390);

		for (const card of cards) {
			const raw = Number.parseFloat(getComputedStyle(card.el).getPropertyValue('--bts-depth'));
			const depth = Number.isNaN(raw) ? 0.6 : raw;

			card.amp = FLOAT.amp * unit;
			card.phaseX = card.el.offsetLeft * FLOAT.phase;
			card.phaseY = card.el.offsetTop * FLOAT.phase;
			card.danceAmp = DANCE.amp * (0.45 + depth * 0.7) * unit;
			card.followRate = DANCE.followFar + (DANCE.followNear - DANCE.followFar) * depth;

			gsap.set(card.el, {
				z: (depth - 0.55) * 160 * unit,
				scale: 0.9 + depth * 0.18,
				force3D: true,
			});
		}
	};

	const drift = (delta) => {
		elapsed += delta;

		const t = elapsed * FLOAT.speed;
		const dt = Math.min(DANCE.maxStep, delta / 1000);

		for (const card of cards) {
			const k = 1 - Math.exp(-card.followRate * dt);

			card.danceX += (danceTx - card.danceX) * k;
			card.danceY += (danceTy - card.danceY) * k;

			gsap.set(card.el, {
				x: (Math.sin(t + card.phaseX) * card.amp + card.danceX * card.danceAmp) * ramp.value,
				y: (Math.cos(t + card.phaseY) * card.amp + card.danceY * card.danceAmp) * ramp.value,
			});
		}
	};

	const setDance = (x, y) => {
		danceTx = x;
		danceTy = y;
	};

	const onPointerMove = (event) => {
		if (!desktop.matches || event.pointerType === 'touch' || reduce.matches) return;

		const rect = frame.getBoundingClientRect();
		if (!rect.width || !rect.height) return;

		setDance(
			clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1),
			clamp(((event.clientY - rect.top) / rect.height) * 2 - 1, -1, 1)
		);
	};

	const stopFloat = () => {
		if (!ticker) return;

		gsap.ticker.remove(ticker);
		ticker = null;
	};

	const startFloat = () => {
		if (ticker || reduce.matches) return;

		if (!ramp.value) gsap.to(ramp, { value: 1, duration: FLOAT.ramp, ease: 'power2.out' });

		ticker = (time, delta) => drift(delta);
		gsap.ticker.add(ticker);
	};

	const finish = () => {
		root.classList.add('is-revealed');
		startFloat();
	};

	const playIntro = () => {
		if (reduce.matches) {
			gsap.set(reveals, { scale: 1 });
			gsap.set(swatches, { opacity: 0 });
			gsap.set(media, { opacity: 1 });
			finish();
			return;
		}

		const step = { duration: INTRO.duration, ease: INTRO.ease, stagger: INTRO.stagger };

		gsap
			.timeline({ onComplete: finish })
			.to(reveals, { scale: 1, ...step }, 0)
			.to(swatches, { opacity: 0, ...step }, 0)
			.to(media, { opacity: 1, ...step }, 0);
	};

	measure();
	gsap.set(media, { opacity: 0 });

	let started = false;

	if ('IntersectionObserver' in window) {
		new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) stopFloat();
					else if (!started) {
						started = true;
						playIntro();
					} else if (root.classList.contains('is-revealed')) startFloat();
				}
			},
			{ threshold: INTRO.threshold }
		).observe(root);
	} else {
		playIntro();
	}

	reduce.addEventListener('change', () => {
		if (!reduce.matches) {
			if (root.classList.contains('is-revealed')) startFloat();
			return;
		}

		stopFloat();
		gsap.killTweensOf(ramp);
		ramp.value = 0;
		setDance(0, 0);

		for (const card of cards) {
			card.danceX = 0;
			card.danceY = 0;
		}

		gsap.set(cards.map((card) => card.el), { x: 0, y: 0 });
	});

	frame.addEventListener('pointermove', onPointerMove);
	frame.addEventListener('pointerleave', () => setDance(0, 0));

	desktop.addEventListener('change', () => {
		if (desktop.matches) return;

		setDance(0, 0);
	});

	if ('ResizeObserver' in window) new ResizeObserver(measure).observe(frame);
	else addEventListener('resize', measure);
};

const initAll = (scope) => {
	whenGsapReady(() => {
		for (const root of scope.querySelectorAll(SECTION_SELECTOR)) initSection(root);
	});
};

initAll(document);

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
